import { P2GAlert, P2GGrid, P2GLink, P2GText, SvgEnquiryError } from '@parcel2go/core-ui';
import { ChatBase, useTenant } from '@parcel2go/npm-p2g-app';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { TranslationNamespaces } from '~/lib/constants';
import { Tracking } from '~/lib/types/tracking-types';
import { PublicSettings } from '~/lib/ui-serverless-settings';
import {
  GetLastTrackingEventTimeStamp,
  GetRelativeTimestampByDays,
  HasEstimatedDeliveryDatePassed
} from '~/lib/utils/tracking';

interface ITrackingIssueMessageProps {
  color?: 'info' | 'warning' | 'error' | 'success';
  headingVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  helpLink?: string;
  infoLink?: string;
  message?: string | React.JSX.Element; // specifically <Trans/> component
  orderReference: string | number;
  showChat?: boolean;
  showContact?: boolean;
  showOptions: boolean;
  title?: string | React.JSX.Element; // specifically <Trans/> component
  onValidateOrder?: () => void;
}

const useStyles = makeStyles()(() => ({
  iconColour: {
    fill: '#b01515',
    '& svg>path': {
      fill: '#b01515'
    },
    '&>.MuiGrid-container': {
      flexWrap: 'nowrap'
    }
  }
}));

export const TrackingIssueMessage = ({
  color,
  headingVariant,
  helpLink,
  infoLink,
  message,
  orderReference,
  showChat,
  showContact,
  showOptions,
  title,
  onValidateOrder
}: ITrackingIssueMessageProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation(TranslationNamespaces.TRACKING);

  return (
    <>
      {title && (
        <P2GGrid container spacing={1}>
          <P2GGrid item>
            <P2GText variant={headingVariant} data-testid="tracking-notification-heading">
              {title}
            </P2GText>
          </P2GGrid>
        </P2GGrid>
      )}
      {color !== 'info' && (
        <P2GAlert
          classNames={classes.iconColour}
          sx={{ py: 2, mt: 2 }}
          color={color ?? 'info'}
          icon={color === 'error' ? <SvgEnquiryError /> : ''}
        >
          <P2GGrid container spacing={1} wrap="nowrap" direction="column">
            <P2GGrid item>{message}</P2GGrid>
            {showOptions && showChat && (
              <P2GGrid item>
                <ChatBase orderReference={orderReference.toString()}>
                  <Trans
                    t={t}
                    i18nKey="RESULT_ISSUE_CHAT"
                    defaults="Please help us resolve the issue by contacting our team via Live Chat"
                  />
                </ChatBase>
              </P2GGrid>
            )}
            {showOptions && showChat && (helpLink || infoLink) && <P2GGrid item>/</P2GGrid>}
            {showOptions && helpLink && (
              <P2GGrid item>
                <P2GLink href={helpLink}>
                  <Trans t={t} i18nKey="RESULT_ISSUE_HELP" defaults="Lets try and fix this" />
                </P2GLink>
              </P2GGrid>
            )}
            {showOptions && infoLink && (
              <P2GGrid item>
                <P2GLink href={infoLink}>
                  <Trans t={t} i18nKey="RESULT_ISSUE_INFO" defaults="For more information click here" />
                </P2GLink>
              </P2GGrid>
            )}
            {showOptions && showContact && (
              <P2GGrid item>
                <Trans
                  t={t}
                  i18nKey="RESULT_ISSUE_CONTACT"
                  defaults="No need to get in touch, we will email you shortly with an update."
                />
              </P2GGrid>
            )}
            {!showOptions && onValidateOrder && (showChat || showContact || infoLink || helpLink) && (
              <P2GGrid item>
                <P2GLink onClick={onValidateOrder}>
                  <Trans t={t} i18nKey="RESULT_ORDER_VALIDATE_LINK" defaults="More details" />
                </P2GLink>
              </P2GGrid>
            )}
          </P2GGrid>
        </P2GAlert>
      )}
      {color === 'info' && (
        <P2GGrid container spacing={1} wrap="nowrap" direction="column" sx={{ py: '16px' }}>
          <P2GGrid item>{message}</P2GGrid>
          {showOptions && showChat && (
            <P2GGrid item>
              <ChatBase orderReference={orderReference.toString()}>
                <Trans
                  t={t}
                  i18nKey="RESULT_ISSUE_CHAT"
                  defaults="Please help us resolve the issue by contacting our team via Live Chat"
                />
              </ChatBase>
            </P2GGrid>
          )}
          {showOptions && showChat && (helpLink || infoLink) && <P2GGrid item>/</P2GGrid>}
          {showOptions && helpLink && (
            <P2GGrid item>
              <P2GLink href={helpLink}>
                <Trans t={t} i18nKey="RESULT_ISSUE_HELP" defaults="Lets try and fix this" />
              </P2GLink>
            </P2GGrid>
          )}
          {showOptions && infoLink && (
            <P2GGrid item>
              <P2GLink href={infoLink}>
                <Trans t={t} i18nKey="RESULT_ISSUE_INFO" defaults="For more information click here" />
              </P2GLink>
            </P2GGrid>
          )}
          {showOptions && showContact && (
            <P2GGrid item>
              <Trans
                t={t}
                i18nKey="RESULT_ISSUE_CONTACT"
                defaults="No need to get in touch, we will email you shortly with an update."
              />
            </P2GGrid>
          )}
          {!showOptions && onValidateOrder && (showChat || showContact || infoLink || helpLink) && (
            <P2GGrid item>
              <P2GLink onClick={onValidateOrder}>
                <Trans t={t} i18nKey="RESULT_ORDER_VALIDATE_LINK" defaults="More details" />
              </P2GLink>
            </P2GGrid>
          )}
        </P2GGrid>
      )}
    </>
  );
};

interface ITrackingIssue {
  courierName: string;
  headingVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  tracking: Tracking;
  onOpenValidation: () => void;
}

const TrackingIssues = ({ courierName, headingVariant, tracking, onOpenValidation }: ITrackingIssue) => {
  const { t } = useTranslation(TranslationNamespaces.TRACKING);
  const id = tracking.order.orderLineId;

  const sharedProps = {
    showOptions: true,
    onValidateOrder: onOpenValidation
  };
  const isDomestic = tracking.order.collectionCountryIso2Code === tracking.order.deliveryCountryIso2Code;

  const lastTrackingEventScan = GetLastTrackingEventTimeStamp(tracking);
  const showHeldByCustomsDelayedMessage = lastTrackingEventScan
    ? tracking.hasHeldInCustomsParcel &&
      lastTrackingEventScan.getTime() < GetRelativeTimestampByDays(4) &&
      tracking.isOwner
    : false;
  const estimatedDeliveryHasPassed = HasEstimatedDeliveryDatePassed(tracking.order.estimatedDeliveryDate);
  const isNotTrackingInTransit = lastTrackingEventScan
    ? !tracking.complete &&
      !estimatedDeliveryHasPassed &&
      !tracking.hasRTSParcel &&
      !tracking.hasHeldInCustomsParcel &&
      lastTrackingEventScan.getTime() < GetRelativeTimestampByDays(4)
    : false;

  const { settings } = useTenant();
  const websiteUrl = settings[PublicSettings.WEBSITE];

  return (
    <>
      {tracking.hasDamagedParcel && (
        <TrackingIssueMessage
          color="error"
          orderReference={id}
          title={t('RESULT_ISSUE_DAMAGE_TITLE', 'Your parcel has been damaged')}
          message={t('RESULT_ISSUE_DAMAGE', 'The courier notified us that your parcel is damaged.')}
          showChat={true}
          showContact={false}
          headingVariant={headingVariant}
          {...sharedProps}
        />
      )}

      {!tracking.complete && tracking.hasLostParcel && (
        <TrackingIssueMessage
          color="error"
          orderReference={id}
          title={t('RESULT_ISSUE_LOST_TITLE', 'The courier has lost your parcel')}
          message={t(
            'RESULT_ISSUE_LOST',
            'The courier notified us that they lost your parcel. No need to get in touch, we will contact you shortly with an update.'
          )}
          showChat={false}
          showContact={false}
          headingVariant={headingVariant}
          {...sharedProps}
        />
      )}

      {!tracking.complete &&
        tracking.hasDelayedParcel &&
        !tracking.hasAddressIssueParcel &&
        !tracking.hasDamagedParcel && (
          <TrackingIssueMessage
            color="info"
            orderReference={id}
            title={t('RESULT_ISSUE_DELAY_TITLE', 'There has been a delay with your parcel')}
            message={t(
              'RESULT_ISSUE_DELAY',
              'The courier notified us that your parcel is delayed. Please allow a further 48 hours for delivery.'
            )}
            showChat={false}
            showContact={false}
            headingVariant={headingVariant}
            {...sharedProps}
          />
        )}

      {!tracking.complete &&
        tracking.hasHeldInCustomsParcel &&
        !tracking.hasRTSParcel &&
        !showHeldByCustomsDelayedMessage && (
          <TrackingIssueMessage
            color="error"
            orderReference={id}
            title={t('RESULT_ISSUE_HELD_CUSTOMS_TITLE', {
              defaultValue: 'Your {{courier}} parcel is being held at customs',
              courier: courierName
            })}
            message={
              <Trans
                t={t}
                i18nKey="RESULT_ISSUE_HELD_CUSTOMS"
                defaults="The parcel is currently with customs and should resume tracking once cleared. The sender can view more information on customs and request support by <0>clicking here</0>. If you are the recipient, please look out for communications direct from customs authorities and/or contact the sender for updates."
                components={[<P2GLink href={`${websiteUrl}/service-centre/caseheldcustoms`} />]}
              />
            }
            showChat={false}
            showContact={false}
            headingVariant={headingVariant}
            {...sharedProps}
          />
        )}

      {!tracking.complete && showHeldByCustomsDelayedMessage && !tracking.hasRTSParcel && (
        <TrackingIssueMessage
          color="error"
          orderReference={id}
          title={t('RESULT_ISSUE_HELD_CUSTOMS_TITLE', {
            defaultValue: 'Your {{courier}} parcel is being held at customs',
            courier: courierName
          })}
          message={
            <Trans
              t={t}
              i18nKey="RESULT_ISSUE_HELD_CUSTOMS_DELAY"
              defaults="The parcel is currently with customs and should resume tracking once cleared. The sender can request help with customs by <0>clicking here</0> if they have any concerns. If you are the recipient, please contact the sender for any support and/or updates."
              components={[
                <P2GLink
                  href={`${websiteUrl}/service-centre/casetrackingdelaysandissues?selectedOrderline=${tracking.order.orderLineId}`}
                />
              ]}
            />
          }
          showChat={false}
          showContact={false}
          headingVariant={headingVariant}
          {...sharedProps}
        />
      )}

      {!tracking.complete && tracking.hasProhibitedParcel && (
        <TrackingIssueMessage
          color="error"
          orderReference={id}
          title={t('RESULT_ISSUE_PROHIBITED_TITLE', 'Your parcel contains a prohibited item')}
          message={
            <Trans
              t={t}
              i18nKey="RESULT_ISSUE_PROHIBITED"
              defaults="The courier has notified us that your parcel contains a prohibited item.  No need to get in touch, we will contact you shortly with an update.  <0>For more information on prohibited items, please click here</0>"
              components={[<P2GLink href={`${websiteUrl}/prohibited-items`} />]}
            />
          }
          showChat={false}
          showContact={false}
          headingVariant={headingVariant}
          {...sharedProps}
        />
      )}

      {!tracking.complete && tracking.hasOversizeParcel && (
        <TrackingIssueMessage
          color="error"
          orderReference={id}
          title={t('RESULT_ISSUE_OVERSIZE_TITLE', 'Your parcel is too large')}
          message={
            <Trans
              t={t}
              i18nKey="RESULT_ISSUE_OVERSIZE"
              defaults="The courier notified us that your parcel is too large. If the weight and dimensions of your parcel are within the restrictions, it may be due to the parcel's volumetric weight.  No need to get in touch, we will contact you shortly with more details.  <0>For more information on volumetric weight click here.</0>"
              components={[
                <P2GLink
                  href={`${websiteUrl}/help-centre/parcels/question/packaging/how-to-weigh-and-measure-a-parcel`}
                />
              ]}
            />
          }
          showChat={false}
          showContact={false}
          headingVariant={headingVariant}
          {...sharedProps}
        />
      )}

      {!tracking.complete && tracking.hasRTSParcel && isDomestic && (
        <TrackingIssueMessage
          color="error"
          orderReference={id}
          title={t('RESULT_ISSUE_RTS_TITLE', 'This parcel is being returned to the sender')}
          message={
            <Trans
              t={t}
              i18nKey="RESULT_ISSUE_RTS"
              defaults="Depending on the courier, the return of this parcel may take up to 2 weeks. Please <0>raise an enquiry</0> after this time if it hasn't been received."
              components={[
                <P2GLink href={`${websiteUrl}/service-centre/select/choose?utm_content=RTS_Enquiry_Link_Domestic`} />
              ]}
            />
          }
          showChat={false}
          showContact={false}
          headingVariant={headingVariant}
          {...sharedProps}
        />
      )}

      {!tracking.complete && tracking.hasRTSParcel && !isDomestic && (
        <TrackingIssueMessage
          color="error"
          orderReference={id}
          title={t('RESULT_ISSUE_RTS_TITLE', 'This parcel is being returned to the sender')}
          message={
            <Trans
              t={t}
              i18nKey="RESULT_ISSUE_RTS_INTERNATIONAL"
              defaults="Depending on the courier, the return of this parcel may take up to 6 weeks. Please <0>raise an enquiry</0> after this time if it hasn't been received."
              components={[
                <P2GLink
                  href={`${websiteUrl}/service-centre/select/choose?utm_content=RTS_Enquiry_Link_International`}
                />
              ]}
            />
          }
          showChat={false}
          showContact={false}
          headingVariant={headingVariant}
          {...sharedProps}
        />
      )}

      {!tracking.complete && tracking.hasAddressIssueParcel && (
        <TrackingIssueMessage
          color="error"
          orderReference={id}
          title={t('RESULT_ISSUE_ADDRESS_ISSUE_TITLE', 'There is an issue with the delivery address')}
          message={t(
            'RESULT_ISSUE_ADDRESS_ISSUE',
            'The courier has notified us that they are having problems with the delivery address. There could be several reasons for this.'
          )}
          showChat={true}
          showContact={false}
          headingVariant={headingVariant}
          {...sharedProps}
        />
      )}

      {!tracking.complete && tracking.hasFailedDeliveryParcel && !tracking.hasRTSParcel && (
        <TrackingIssueMessage
          color="error"
          orderReference={id}
          title={t('RESULT_ISSUE_FAILED_DELIVERY_TITLE', 'The courier failed to deliver your parcel')}
          message={t(
            'RESULT_ISSUE_FAILED_DELIVERY',
            'The courier has notified us that they have failed to deliver the parcel. There could be several reasons for this.'
          )}
          showChat={true}
          showContact={false}
          headingVariant={headingVariant}
          {...sharedProps}
        />
      )}

      {!tracking.complete &&
        tracking.hasAttemptedDeliveryParcel &&
        !tracking.hasFailedDeliveryParcel &&
        !tracking.hasRTSParcel && (
          <TrackingIssueMessage
            color="info"
            orderReference={id}
            title={t('RESULT_ISSUE_ATTEMPTED_DELIVERY_TITLE', 'The courier attempted to deliver your parcel')}
            message={t(
              'RESULT_ISSUE_ATTEMPTED_DELIVERY',
              'The courier attempted to make the delivery but was unsuccessful. Please allow up to 24hrs for the tracking to update.'
            )}
            showChat={false}
            showContact={false}
            headingVariant={headingVariant}
            {...sharedProps}
          />
        )}

      {!tracking.hasFailedDeliveryParcel &&
        !tracking.hasAttemptedDeliveryParcel &&
        !tracking.hasDamagedParcel &&
        !tracking.hasLostParcel &&
        !tracking.hasDelayedParcel &&
        !tracking.hasHeldInCustomsParcel &&
        !tracking.hasProhibitedParcel &&
        !tracking.hasOversizeParcel &&
        !tracking.hasRTSParcel &&
        !tracking.hasAddressIssueParcel &&
        !tracking.isLate &&
        !tracking.possiablyLost &&
        !isNotTrackingInTransit && (
          <TrackingIssueMessage
            color="error"
            orderReference={id}
            title={t('RESULT_ISSUE_GENERIC_TITLE', 'There is an issue with your parcel')}
            message={t(
              'RESULT_ISSUE_GENERIC',
              'The courier has notified us that there is an issue with your parcel.  No need to get in touch, we will contact you shortly with an update.'
            )}
            showChat={false}
            showContact={false}
            headingVariant={headingVariant}
            {...sharedProps}
          />
        )}

      {!tracking.complete &&
        !tracking.hasRTSParcel &&
        isNotTrackingInTransit &&
        !(tracking.isLate && !tracking.hasDelayedParcel && !tracking.hasAddressIssueParcel) && (
          <TrackingIssueMessage
            color="info"
            orderReference={id}
            title={t('RESULT_ISSUE_IN_TRANSIT_TITLE', 'Status: In Transit')}
            message={t(
              'RESULT_ISSUE_IN_TRANSIT_NOT_TRACKED',
              'Your parcel is in transit and within the estimated delivery date. Tracking updates will be provided shortly.'
            )}
            showChat={false}
            showContact={false}
            headingVariant={headingVariant}
            {...sharedProps}
          />
        )}

      {!tracking.complete &&
        !tracking.hasDamagedParcel &&
        !tracking.hasFailedDeliveryParcel &&
        !tracking.possiablyLost &&
        !tracking.hasRTSParcel &&
        tracking.isLate &&
        !tracking.hasDelayedParcel &&
        !tracking.hasAddressIssueParcel &&
        !showHeldByCustomsDelayedMessage && (
          <TrackingIssueMessage
            color="info"
            title={t('RESULT_ISSUE_IN_TRANSIT_TITLE', 'Status: In Transit')}
            message={t(
              'RESULT_ISSUE_IN_TRANSIT_LATE',
              'We are very sorry that your parcel has not yet been delivered.'
            )}
            orderReference={id}
            showChat={false}
            headingVariant={headingVariant}
            {...sharedProps}
          />
        )}

      {!tracking.complete &&
        !tracking.hasDamagedParcel &&
        !tracking.hasRTSParcel &&
        !tracking.hasHeldInCustomsParcel &&
        tracking.possiablyLost &&
        !showHeldByCustomsDelayedMessage && (
          <TrackingIssueMessage
            color="info"
            title={t('RESULT_ISSUE_IN_TRANSIT_TITLE', 'Status: In Transit')}
            message={
              <Trans
                t={t}
                i18nKey="RESULT_ISSUE_IN_TRANSIT_POSSIBLY_LOST"
                defaults="We apologise for the delay with your delivery. It remains In Transit and will be delivered as soon as possible.  If you require any support locating your parcel, then the sender can <0>click here</0> to open an enquiry."
                components={[
                  <P2GLink
                    href={`${websiteUrl}/service-centre/select?caseType=NotDelivered${tracking.isOwner && `&id=${tracking.order.orderLineId}`}`}
                  />
                ]}
              />
            }
            orderReference={id}
            showChat={false}
            headingVariant={headingVariant}
            {...sharedProps}
          />
        )}
    </>
  );
};

export default TrackingIssues;
